import React, { createContext, useContext, useEffect, useState } from "react";
import { User, Session, Provider } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase.js";
import { backfillUserVideos } from "../services/videoService.js";
import { getApiBaseUrl } from "../services/api.js";

export interface HelixUserProfile {
  id: string;
  email?: string;
  displayName: string;
  handle: string;
  avatarUrl?: string;
  provider?: string;
  isGithubUser: boolean;
  role?: "ADMIN" | "CREATOR" | "USER";
}

export interface HelixChannel {
  id: string;
  user_id?: string;
  channel_name: string;
  handle: string;
  description?: string;
  banner_url?: string;
  avatar_url?: string;
  category?: string;
  is_verified?: boolean;
  subscriber_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface AuthResponse {
  user: User | null;
  session: Session | null;
  error: Error | null;
}

export interface CreateChannelParams {
  channelName: string;
  handle: string;
  description?: string;
  avatarUrl?: string;
  bannerUrl?: string;
  category?: string;
}

export interface AuthContextType {
  user: User | null;
  profile: HelixUserProfile | null;
  session: Session | null;
  channel: HelixChannel | null;
  hasChannel: boolean;
  isLoading: boolean;
  isAuthModalOpen: boolean;
  setIsAuthModalOpen: (open: boolean) => void;
  isCreateChannelModalOpen: boolean;
  setIsCreateChannelModalOpen: (open: boolean) => void;
  signInWithPassword: (email: string, password: string) => Promise<AuthResponse>;
  signUp: (email: string, password: string, displayName?: string) => Promise<AuthResponse>;
  signInWithOAuth: (provider: "github" | "google") => Promise<void>;
  signInWithGithub: () => Promise<void>;
  signOut: () => Promise<void>;
  createChannel: (params: CreateChannelParams) => Promise<{ success: boolean; channel?: HelixChannel; error?: Error }>;
  refreshChannel: () => Promise<HelixChannel | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<HelixUserProfile | null>(null);
  const [channel, setChannel] = useState<HelixChannel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isCreateChannelModalOpen, setIsCreateChannelModalOpen] = useState(false);

  const buildProfileFromUser = (u: User): HelixUserProfile => {
    const meta = u.user_metadata || {};
    const rawHandle =
      meta.user_name ||
      meta.preferred_username ||
      u.email?.split("@")[0] ||
      "helixcreator";
    const displayName =
      meta.full_name ||
      meta.name ||
      meta.display_name ||
      rawHandle ||
      "HP - Helix Member";
    const avatarUrl =
      meta.avatar_url ||
      meta.picture ||
      (u.app_metadata?.provider === "github" ? `https://github.com/${rawHandle}.png` : undefined);

    const explicitRole =
      (u as any).role ||
      meta.role ||
      u.app_metadata?.role;

    const normalizedRole = typeof explicitRole === "string" ? explicitRole.toUpperCase() : "";

    const role: "ADMIN" | "CREATOR" | "USER" =
      normalizedRole === "ADMIN" ||
      u.email === "isabish3@gmail.com" ||
      u.email === "admin@hphelix.com" ||
      u.email === "root@hphelix.com"
        ? "ADMIN"
        : normalizedRole === "CREATOR" ||
          rawHandle.toLowerCase().includes("joviali") ||
          u.email === "jovial@hphelix.com"
        ? "CREATOR"
        : "USER";

    (u as any).role = role;

    return {
      id: u.id,
      email: u.email,
      displayName,
      handle: rawHandle,
      avatarUrl,
      provider: u.app_metadata?.provider || "email",
      isGithubUser: u.app_metadata?.provider === "github" || !!meta.avatar_url,
      role,
    };
  };

  const fetchChannelForUser = async (
    userId?: string,
    handle?: string
  ): Promise<HelixChannel | null> => {
    if (!userId && !handle) return null;
    const cleanHandle = handle ? handle.replace(/^@/, "").toLowerCase() : undefined;
    const storageKey = userId ? `helix_channel_${userId}` : `helix_channel_${cleanHandle}`;

    // 1. Check LocalStorage
    if (typeof localStorage !== "undefined") {
      try {
        const cached = localStorage.getItem(storageKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && parsed.channel_name) {
            return parsed;
          }
        }
      } catch {}
    }

    // 2. Fetch from Supabase channels table
    try {
      let data: any = null;
      let error: any = null;

      if (userId) {
        const res = await supabase.from("channels").select("*").eq("user_id", userId).maybeSingle();
        data = res.data;
        error = res.error;
      }

      if (!data && cleanHandle) {
        const res = await supabase
          .from("channels")
          .select("*")
          .or(`handle.eq.${cleanHandle},handle.eq.@${cleanHandle},handle.ilike.%${cleanHandle}%,channel_name.ilike.%${cleanHandle}%`)
          .maybeSingle();
        data = res.data;
        error = res.error;
      }

      if (!error && data) {
        const ch: HelixChannel = {
          id: data.id,
          user_id: data.user_id,
          channel_name: data.channel_name || (data as any).channelName || "My Channel",
          handle: data.handle,
          description: data.description,
          banner_url: data.banner_url || (data as any).bannerUrl,
          avatar_url: data.avatar_url || (data as any).avatarUrl,
          category: data.category || "Music",
          is_verified: data.is_verified || false,
          subscriber_count: data.subscriber_count || 0,
          created_at: data.created_at,
          updated_at: data.updated_at,
        };

        if (typeof localStorage !== "undefined") {
          try {
            if (userId) localStorage.setItem(`helix_channel_${userId}`, JSON.stringify(ch));
            localStorage.setItem(`helix_channel_${ch.handle}`, JSON.stringify(ch));
          } catch {}
        }
        return ch;
      }
    } catch {}

    // 3. Fallback check from creator_profiles
    try {
      let cpQuery = supabase.from("creator_profiles").select("*");
      if (userId) {
        cpQuery = cpQuery.eq("user_id", userId);
      } else if (cleanHandle) {
        cpQuery = cpQuery.eq("handle", cleanHandle);
      }

      const { data: cpData, error: cpErr } = await cpQuery.single();
      if (!cpErr && cpData && (cpData.display_name || cpData.handle)) {
        const ch: HelixChannel = {
          id: cpData.id,
          user_id: cpData.user_id,
          channel_name: cpData.display_name || (cpData as any).displayName || "My Channel",
          handle: cpData.handle,
          description: cpData.bio,
          banner_url: cpData.banner_url || (cpData as any).bannerUrl,
          avatar_url: cpData.avatar_url || (cpData as any).avatarUrl,
          category: "Music",
          is_verified: false,
          subscriber_count: 0,
          created_at: cpData.created_at,
        };
        return ch;
      }
    } catch {}

    // 4. Fallback check from Fastify API /api/v1/channels/:handle
    try {
      const apiBase = getApiBaseUrl();
      const lookupTarget = cleanHandle || userId;
      if (lookupTarget) {
        const res = await fetch(`${apiBase}/api/v1/channels/${encodeURIComponent(lookupTarget)}`);
        if (res.ok) {
          const body = await res.json();
          if (body?.success && body?.data) {
            const ch: HelixChannel = {
              id: body.data.id,
              user_id: body.data.user_id || body.data.userId || userId,
              channel_name: body.data.channel_name || body.data.channelName || "My Channel",
              handle: body.data.handle?.startsWith("@") ? body.data.handle : `@${body.data.handle}`,
              description: body.data.description,
              banner_url: body.data.banner_url || body.data.bannerUrl,
              avatar_url: body.data.avatar_url || body.data.avatarUrl,
              category: body.data.category || "Music",
              is_verified: body.data.is_verified || body.data.isVerified || false,
              subscriber_count: body.data.subscriber_count || body.data.subscriberCount || 0,
              created_at: body.data.created_at || body.data.createdAt,
              updated_at: body.data.updated_at || body.data.updatedAt,
            };
            if (typeof localStorage !== "undefined") {
              try {
                if (userId) localStorage.setItem(`helix_channel_${userId}`, JSON.stringify(ch));
                localStorage.setItem(`helix_channel_${ch.handle}`, JSON.stringify(ch));
              } catch {}
            }
            return ch;
          }
        }
      }
    } catch {}

    return null;
  };

  const refreshChannel = async (): Promise<HelixChannel | null> => {
    if (!user) {
      setChannel(null);
      return null;
    }
    const ch = await fetchChannelForUser(user.id, profile?.handle);
    setChannel(ch);
    return ch;
  };

  useEffect(() => {
    const restoreSession = async (currentSession: Session | null) => {
      let activeSession = currentSession;
      if (!activeSession && typeof localStorage !== "undefined") {
        try {
          const savedSession = localStorage.getItem("helix_auth_session");
          if (savedSession) {
            activeSession = JSON.parse(savedSession);
          }
        } catch {}
      }

      setSession(activeSession);
      if (activeSession?.user) {
        setUser(activeSession.user);
        const p = buildProfileFromUser(activeSession.user);
        setProfile(p);
        if (typeof localStorage !== "undefined") {
          try {
            localStorage.setItem("helix_auth_user", JSON.stringify(p));
          } catch {}
        }

        const userChannel = await fetchChannelForUser(activeSession.user.id, p.handle);
        setChannel(userChannel);
      } else {
        setUser(null);
        setProfile(null);
        setChannel(null);
        if (typeof localStorage !== "undefined") {
          try {
            localStorage.removeItem("helix_auth_user");
            localStorage.removeItem("helix_auth_session");
          } catch {}
        }
      }
      setIsLoading(false);
    };

    // 1. Check existing active session with error safeguard
    supabase.auth
      .getSession()
      .then(async ({ data }) => {
        await restoreSession(data?.session || null);
      })
      .catch(async (err) => {
        console.warn("[auth] Supabase getSession network notice:", err);
        // Fallback to local storage session
        await restoreSession(null);
      });

    // 2. Listen to Supabase auth state changes
    let subscription: any = null;
    try {
      const authListener = supabase.auth.onAuthStateChange(async (_event, newSession) => {
        setSession(newSession);
        if (newSession?.user) {
          setUser(newSession.user);
          const p = buildProfileFromUser(newSession.user);
          setProfile(p);
          if (typeof localStorage !== "undefined") {
            try {
              localStorage.setItem("helix_auth_user", JSON.stringify(p));
            } catch {}
          }

          const userChannel = await fetchChannelForUser(newSession.user.id, p.handle);
          setChannel(userChannel);
        } else if (!newSession && typeof localStorage !== "undefined" && localStorage.getItem("helix_auth_session")) {
          // Keep local storage session if Supabase emits null on network glitch
        } else {
          setUser(null);
          setProfile(null);
          setChannel(null);
          if (typeof localStorage !== "undefined") {
            try {
              localStorage.removeItem("helix_auth_user");
            } catch {}
          }
        }
        setIsLoading(false);
      });
      subscription = authListener?.data?.subscription;
    } catch (e) {
      console.warn("[auth] onAuthStateChange setup notice:", e);
    }

    return () => {
      try {
        subscription?.unsubscribe?.();
      } catch {}
    };
  }, []);

  const createChannel = async (
    params: CreateChannelParams
  ): Promise<{ success: boolean; channel?: HelixChannel; error?: Error }> => {
    const cleanHandle = params.handle.replace(/^@/, "").toLowerCase().trim();
    const name = params.channelName.trim();

    if (!name || !cleanHandle) {
      return {
        success: false,
        error: new Error("Channel name and handle are required."),
      };
    }

    const defaultAvatar =
      params.avatarUrl ||
      profile?.avatarUrl ||
      `https://api.dicebear.com/7.x/identicon/svg?seed=${cleanHandle}`;

    const channelObj: HelixChannel = {
      id: `chn_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      user_id: user?.id,
      channel_name: name,
      handle: cleanHandle,
      description: params.description?.trim() || "",
      avatar_url: defaultAvatar,
      banner_url: params.bannerUrl || "",
      category: params.category || "Music",
      is_verified: false,
      subscriber_count: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // 1. Immediately cache locally
    if (typeof localStorage !== "undefined") {
      try {
        if (user?.id) localStorage.setItem(`helix_channel_${user.id}`, JSON.stringify(channelObj));
        localStorage.setItem(`helix_channel_${cleanHandle}`, JSON.stringify(channelObj));
        localStorage.setItem(`helix_bio_${cleanHandle}`, params.description?.trim() || "");
        if (params.bannerUrl) localStorage.setItem(`helix_banner_${cleanHandle}`, params.bannerUrl);
      } catch {}
    }

    // 2. Persist to Supabase public.channels and creator_profiles
    try {
      const { data: dbChannel } = await supabase
        .from("channels")
        .upsert(
          {
            id: channelObj.id,
            user_id: user?.id,
            channel_name: name,
            handle: cleanHandle.startsWith("@") ? cleanHandle : `@${cleanHandle}`,
            description: params.description?.trim(),
            avatar_url: defaultAvatar,
            banner_url: params.bannerUrl,
            category: params.category || "Music",
            updated_at: new Date().toISOString(),
          },
          { onConflict: "handle" }
        )
        .select()
        .maybeSingle();

      if (dbChannel && dbChannel.id) {
        channelObj.id = dbChannel.id;
        if (typeof localStorage !== "undefined") {
          try {
            if (user?.id) localStorage.setItem(`helix_channel_${user.id}`, JSON.stringify(channelObj));
            localStorage.setItem(`helix_channel_${cleanHandle}`, JSON.stringify(channelObj));
          } catch {}
        }
      }

      await supabase.from("creator_profiles").upsert(
        {
          user_id: user?.id,
          handle: cleanHandle,
          display_name: name,
          bio: params.description?.trim(),
          avatar_url: defaultAvatar,
          banner_url: params.bannerUrl,
          bannerUrl: params.bannerUrl,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "handle" }
      );
    } catch (dbErr) {
      console.warn("[channel] Supabase channel sync notice:", dbErr);
    }

    // 3. Persist to Fastify API (/api/v1/channels)
    try {
      const apiBase = getApiBaseUrl();
      const res = await fetch(`${apiBase}/api/v1/channels`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          channelName: name,
          handle: cleanHandle,
          description: params.description?.trim() || "",
          avatarUrl: defaultAvatar,
          bannerUrl: params.bannerUrl || "",
          category: params.category || "Music",
          userId: user?.id,
        }),
      });
      if (res.ok) {
        const body = await res.json();
        if (body?.data?.id) {
          channelObj.id = body.data.id;
          if (typeof localStorage !== "undefined") {
            try {
              if (user?.id) localStorage.setItem(`helix_channel_${user.id}`, JSON.stringify(channelObj));
              localStorage.setItem(`helix_channel_${cleanHandle}`, JSON.stringify(channelObj));
            } catch {}
          }
        }
      }
    } catch (apiErr) {
      console.warn("[channel] API channel sync notice:", apiErr);
    }

    setChannel(channelObj);
    backfillUserVideos(channelObj, user?.id);
    return { success: true, channel: channelObj };
  };

  const signInWithPassword = async (email: string, password: string): Promise<AuthResponse> => {
    const apiBase = getApiBaseUrl();

    // 1. Try Supabase Auth
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (!error && data.session && data.user) {
        setUser(data.user);
        setSession(data.session);
        const p = buildProfileFromUser(data.user);
        setProfile(p);
        if (typeof localStorage !== "undefined") {
          try {
            localStorage.setItem("helix_auth_user", JSON.stringify(p));
            localStorage.setItem("helix_auth_session", JSON.stringify(data.session));
          } catch {}
        }
        const userChannel = await fetchChannelForUser(data.user.id, p.handle);
        setChannel(userChannel);
        return { user: data.user, session: data.session, error: null };
      }
    } catch (supabaseErr) {
      console.warn("[auth] Supabase sign-in network notice, falling back to API:", supabaseErr);
    }

    // 2. Fallback: query HP-Helix backend login endpoint (/api/v1/auth/login)
    try {
      const apiRes = await fetch(`${apiBase}/api/v1/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const apiData = await apiRes.json();
      if (apiRes.ok && apiData.success && apiData.token) {
        const authUser: any = {
          id: apiData.user?.id || `usr_${Date.now()}`,
          email: apiData.user?.email || email.trim(),
          user_metadata: {
            role: apiData.role || apiData.user?.role || "USER",
            display_name: apiData.user?.name || email.split("@")[0],
            avatar_url: apiData.user?.avatarUrl,
          },
          app_metadata: {
            role: apiData.role || apiData.user?.role || "USER",
          },
          role: apiData.role || apiData.user?.role || "USER",
        };
        const authSession: any = {
          access_token: apiData.token,
          token_type: "bearer",
          user: authUser,
        };
        setUser(authUser);
        setSession(authSession);
        const p = buildProfileFromUser(authUser);
        setProfile(p);
        if (typeof localStorage !== "undefined") {
          try {
            localStorage.setItem("helix_auth_user", JSON.stringify(p));
            localStorage.setItem("helix_auth_session", JSON.stringify(authSession));
          } catch {}
        }
        const userChannel = await fetchChannelForUser(authUser.id, p.handle);
        setChannel(userChannel);
        return { user: authUser, session: authSession, error: null };
      }

      if (apiData.error) {
        return { user: null, session: null, error: new Error(apiData.error) };
      }
    } catch (apiErr: any) {
      console.warn("[auth] API sign-in notice:", apiErr);
    }

    return { user: null, session: null, error: new Error("Invalid email or password.") };
  };

  const signUp = async (
    email: string,
    password: string,
    displayName?: string
  ): Promise<AuthResponse> => {
    const apiBase = getApiBaseUrl();

    // 1. Try Supabase Auth
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: {
            display_name: displayName || email.split("@")[0],
            full_name: displayName || email.split("@")[0],
          },
        },
      });

      if (!error && data) {
        if (data.session && data.user) {
          setUser(data.user);
          setSession(data.session);
          const p = buildProfileFromUser(data.user);
          setProfile(p);
          if (typeof localStorage !== "undefined") {
            try {
              localStorage.setItem("helix_auth_user", JSON.stringify(p));
              localStorage.setItem("helix_auth_session", JSON.stringify(data.session));
            } catch {}
          }
          const userChannel = await fetchChannelForUser(data.user.id, p.handle);
          setChannel(userChannel);
          return { user: data.user, session: data.session, error: null };
        } else if (data.user) {
          // Awaiting confirmation email
          return { user: data.user, session: null, error: null };
        }
      }
    } catch (supabaseErr) {
      console.warn("[auth] Supabase sign-up network notice, falling back to API:", supabaseErr);
    }

    // 2. Fallback: register via HP-Helix backend endpoint (/api/v1/auth/register)
    try {
      const apiRes = await fetch(`${apiBase}/api/v1/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          password,
          displayName: displayName || email.split("@")[0],
        }),
      });
      const apiData = await apiRes.json();
      if (apiRes.ok && apiData.success && apiData.token) {
        const authUser: any = {
          id: apiData.user?.id || `usr_${Date.now()}`,
          email: apiData.user?.email || email.trim(),
          user_metadata: {
            role: apiData.role || apiData.user?.role || "USER",
            display_name: apiData.user?.name || displayName || email.split("@")[0],
            avatar_url: apiData.user?.avatarUrl,
          },
          app_metadata: {
            role: apiData.role || apiData.user?.role || "USER",
          },
          role: apiData.role || apiData.user?.role || "USER",
        };
        const authSession: any = {
          access_token: apiData.token,
          token_type: "bearer",
          user: authUser,
        };
        setUser(authUser);
        setSession(authSession);
        const p = buildProfileFromUser(authUser);
        setProfile(p);
        if (typeof localStorage !== "undefined") {
          try {
            localStorage.setItem("helix_auth_user", JSON.stringify(p));
            localStorage.setItem("helix_auth_session", JSON.stringify(authSession));
          } catch {}
        }
        const userChannel = await fetchChannelForUser(authUser.id, p.handle);
        setChannel(userChannel);
        return { user: authUser, session: authSession, error: null };
      }

      if (apiData.error) {
        return { user: null, session: null, error: new Error(apiData.error) };
      }
    } catch (apiErr: any) {
      console.warn("[auth] API sign-up notice:", apiErr);
      return { user: null, session: null, error: apiErr };
    }

    return { user: null, session: null, error: new Error("Failed to create account.") };
  };

  const signInWithOAuth = async (provider: "github" | "google") => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider as Provider,
        options: {
          redirectTo: typeof window !== "undefined" ? window.location.origin : undefined,
        },
      });

      if (error) {
        console.warn(`[auth] Supabase ${provider} OAuth notice:`, error.message);
      }
    } catch (err) {
      console.warn(`[auth] ${provider} sign-in exception:`, err);
    }
  };

  const signInWithGithub = async () => {
    await signInWithOAuth("github");
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch {}
    setUser(null);
    setProfile(null);
    setChannel(null);
    setSession(null);
    if (typeof localStorage !== "undefined") {
      try {
        localStorage.removeItem("helix_auth_user");
      } catch {}
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        session,
        channel,
        hasChannel: Boolean(channel),
        isLoading,
        isAuthModalOpen,
        setIsAuthModalOpen,
        isCreateChannelModalOpen,
        setIsCreateChannelModalOpen,
        signInWithPassword,
        signUp,
        signInWithOAuth,
        signInWithGithub,
        signOut,
        createChannel,
        refreshChannel,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
